/**
 * Help article registry.
 * Maps route paths to relevant help articles for contextual help.
 */

export interface HelpArticle {
  id: string;
  title: string;
  category: string;
  content: string;
  route?: string; // Route path this article is relevant to
  routes?: string[]; // Additional route paths this article is relevant to
  keywords: string[];
  actionClaims?: string[];
  guideTargets?: Array<{
    intent: string;
    route: string;
    controlId?: string;
  }>;
}

// ─── Help Articles ─────────────────────────────────────────

export const helpArticles: HelpArticle[] = [
  // Getting Started
  {
    id: "getting-started",
    title: "Getting Started with L30 Insights",
    category: "Getting Started",
    route: "/dashboard",
    keywords: ["start", "begin", "intro", "overview", "dashboard"],
    content: `# Getting Started

Welcome to L30 Insights — your CRM and business operations platform.

## Insights Home
The staff entry is search and subjects at **/**. Canonical knowledge lives on Subject Workspaces. **Dashboard** remains at /dashboard for the previous module home.

## Quick Tour
- **Contacts & Companies**: Manage your CRM contacts, tag them, and link them to companies
- **Documents**: Vault finals, Working drafts, and agreement prepare-for-signature
- **Board Meetings**: Prepare agendas, generate AI commentary, and create board packs
- **E-Signatures**: Native signing after Working → prepare shell → Promote → fields
- **Tracked Email**: Compose and send emails with open/click tracking
- **Cashflow**: Forecast your cash flow with QBO integration
- **Messages**: Communicate with your team in real-time channels
- **Sharing**: Share documents externally with secure links

## Navigation
Use **Jump to…** (⌘K / Ctrl+K) to navigate between features, permissioned **maps**, and **vault documents**. Search synonyms work — e.g. **Atlas**, **maps**, or **hub** for Ideation Maps; **tokens** or **PAT** for Personal Access Tokens. The help panel (? icon) provides context-sensitive help on every page.`,
  },

  // Contacts
  {
    id: "contacts-overview",
    title: "Managing Contacts",
    category: "Contacts",
    route: "/contacts",
    keywords: ["contact", "crm", "people", "add", "create", "search", "tag"],
    content: `# Managing Contacts

## Creating a Contact
Click **Add Contact** and fill in the details. Email is required — first and last name are mandatory.

## Auto-Company Linking
When you create a contact with a company name or a non-free email domain (e.g. @acme.com), the system automatically:
1. Finds an existing company with that name or domain
2. Creates a new company if none exists
3. Links the contact to the company

## Search & Filtering
- Search by name or email using the search bar
- Filter by tag to find specific groups
- Contacts are sorted by name by default

## Bulk Operations
Select multiple contacts to add or remove tags in bulk.

## Merging Contacts
If you have duplicate contacts, use the merge feature. The target contact keeps its data, and the source contact's tags, alternative emails, and custom fields are merged in.`,
  },

  // Companies
  {
    id: "companies-overview",
    title: "Managing Companies",
    category: "Companies",
    route: "/companies",
    keywords: ["company", "organisation", "domain", "sector"],
    content: `# Managing Companies

## Company Features
- **Domains**: Associate email domains with companies for auto-linking
- **Sectors**: Classify companies by industry sector
- **Tags**: Tag companies and propagate tags to all linked contacts
- **Contact Count**: See how many contacts are linked to each company

## Tag Propagation
When you add tags to a company, those tags are automatically added to all contacts linked to that company.`,
  },

  {
    id: "data-rooms-overview",
    title: "Data rooms",
    category: "Projects",
    route: "/data-rooms",
    keywords: ["data room", "proforma", "entity", "investor", "diligence"],
    actionClaims: ["Create room", "Add entity", "Admit", "Release", "Hold", "Record formation"],
    content: `# Data rooms

A data room belongs to one project. Build it before the companies are incorporated.

## Create a room
On Data rooms, choose the project and use **Create room**.

## Proforma entities
Use **Add entity** with a working name and a jurisdiction of Pakistan or the UAE. The registered legal name, registration number, and formation date stay empty. When the company is formed, use **Record formation** on that same entity. Do not add a second entity for the registered name.

## Evidence
Use **Admit** to add a title to the index. Choose the entity, or leave it not yet assigned, and mark the row financial, operational, or both. A new row is held. Use **Release** when it is ready inside the room, or **Hold** to take it back. Release does not invite an investor and does not publish a report.

## Ask Insights
Ask can list rooms, open one room, create a room, add a proforma entity, record formation, admit evidence, and change held or released. Creating, admitting, and recording formation ask you to confirm first.`,
  },

  // Contact groups
  {
    id: "contact-groups-overview",
    title: "Managing Contact Groups",
    category: "Contact Groups",
    route: "/contact-groups",
    keywords: ["group", "audience", "members", "contacts", "campaign"],
    content: `# Managing Contact Groups

Contact groups are reusable audiences for relationship work and campaigns.

## Group Records
- Create a group with a name and description
- Edit group details when the audience purpose changes
- Delete groups that should no longer be used

## Members
Open member management to add or remove contacts. Changes are saved through the group API so campaign audience previews and other downstream workflows see the updated membership.`,
  },

  {
    id: "insights-home-overview",
    title: "Insights Home",
    category: "Insights",
    route: "/",
    keywords: ["search", "subjects", "intelligence hub", "home", "entry"],
    content: `# Insights Home

Home is the staff entry. Search subjects from the graph, open a map, open a Subject Workspace, or use Jump to for modules.

Recent subjects come from nodes you opened and from recently updated graph nodes. Things to know lists open-question nodes only. Dashboard remains at /dashboard.`,
  },
  {
    id: "subject-workspace-overview",
    title: "Subject Workspace",
    category: "Insights",
    route: "/subject",
    keywords: ["subject", "canonical", "facts", "provenance", "related"],
    content: `# Subject Workspace

A Subject Workspace is the reading surface for one knowledge-graph node. Identity, summary, structured facts with source and as-of, related nodes, map memberships, recorded decisions, and attached vault documents come from existing Atlas APIs.

Use **Capture** to add a Fact, Question, Decision, or Note. Facts and decisions live on the Subject node. A Question is an open-question node that questions the Subject (rel = questions; Capture does not write addresses). A Note is a staff note plus an operational association.

**Where are we now?** is Current Position: a generated, cited synthesis of recorded knowledge, or a compact deterministic skeleton when AI is off or ungrounded. It is a view, not a canonical fact. Refreshing synthesis does not change the underlying Subject.

**What needs attention** is deterministic: unanswered questions (unless an \`addresses\` edge exists), competing facts with overlapping as-of, superseded state, pending evidence, and malformed legacy facts. Restricted documents are not listed as pending.

**What changed** lists the last 30 days of graph mutations, association audits, and overlay timestamps — not map commits, and not "since your last visit".

**Link object** associates documents, notes, people, companies, projects, or meetings. That is not the same as vault evidence (sourceRefs). Attach evidence from the Evidence section (Vault document, or promote-and-attach a working document). A document may have both, but each is created explicitly.

Atlas remains the graph editor. Open a map from the workspace to edit. Ask Insights on this page pins the Subject (sourceType=subject), not a containing map.`,
  },

  // Projects
  {
    id: "projects-overview",
    title: "Projects",
    category: "Projects",
    route: "/projects",
    keywords: ["project", "kanban", "gantt", "dependency", "stakeholder", "update", "archive"],
    content: `# Projects

Projects organise delivery work, task boards, timelines, stakeholders, and client updates.

## Partner projects
Pick **Partner** before creating or sharing. Projects live in that partner's schema. **Share with portal users** publishes a read-only plan to that partner's portal Project plan tab (for example gop → gop.portal…/t/gop/portal/project-plan).

## Managing Projects
- Create projects from the project list
- Open a project for overview, Kanban, Gantt, stakeholders, and updates
- Edit project details from the project surface
- Review dependency confidence for missing, blocked, overdue, circular, or unscheduled handoffs
- Admins can archive, restore, and delete where the archived view exposes those actions

## Project Tasks
Use the project Kanban or My tasks to create and edit tasks. Dragging a task between columns persists the status change and reloads from the server if the write fails. Task dependency links must point to tasks in the same project and cannot create cycles.`,
  },

  // My tasks
  {
    id: "my-tasks-overview",
    title: "My Tasks",
    category: "Tasks",
    route: "/tasks",
    keywords: ["task", "assigned", "kanban", "status", "project", "workload", "edit"],
    actionClaims: [
      "New task",
      "Create task",
      "Save changes",
      "Add comment",
      "Export report",
      "Delete task",
    ],
    content: `# My Tasks

My tasks is your cross-project work board.

## Daily Work
- See tasks assigned to you across projects
- Review open workload, due pressure, blocked tasks, status/priority mix, and estimated remaining hours
- Use **Export report** to download the cross-project task report to CSV
- Use **New task** and **Create task** to add a task in a selected project
- Use **Save changes** to edit task title, dates, assignee, priority, dependencies, and status
- Use **Add comment** from the task edit flow when the task needs activity context
- Drag cards between status columns

## Permissions
Task writes still belong to the owning project. Admin-only **Delete task** actions are available from task edit flows where the API permits them.`,
  },

  // CRM lead queue
  {
    id: "crm-leads-overview",
    title: "Lead Queue",
    category: "CRM",
    route: "/crm/leads",
    keywords: ["lead", "queue", "score", "qualify", "disqualify", "crm"],
    content: `# Lead Queue

The lead queue ranks contacts for follow-up using fit, engagement, open-deal, and recent-activity signals.

## Working Leads
- Review the score band and reasons before acting
- Qualify a lead into a deal when there is a live opportunity
- Disqualify a lead with a reason when it should leave the queue

## Trust Signals
Scores are explainable and backed by the CRM scoring policy. Use the visible reasons to decide whether to follow up, qualify, or leave the lead alone.`,
  },

  // CRM deals
  {
    id: "crm-deals-overview",
    title: "Deals",
    category: "CRM",
    route: "/crm/deals",
    keywords: ["deal", "pipeline", "stage", "forecast", "owner", "revenue"],
    content: `# Deals

Deals track active revenue opportunities and weighted forecast value.

## Pipeline Work
- Create and edit deal records
- Move deals through pipeline stages
- Set owner, expected close date, value, probability, and source details
- Archive, restore, or delete deals where admin permissions allow it

## Forecasting
Deal value feeds revenue reporting and cashflow pipeline weighting when that setting is enabled.`,
  },

  // CRM campaigns
  {
    id: "crm-campaigns-overview",
    title: "Campaigns",
    category: "CRM",
    route: "/crm/campaigns",
    keywords: ["campaign", "template", "segment", "send", "analytics", "unsubscribe"],
    content: `# Campaigns

Campaigns combine reusable email templates, static or dynamic audiences, suppression checks, sends, and engagement analytics.

## Authoring
- Draft a campaign from a template or custom content
- Preview the audience before sending
- Respect unsubscribe, bounce, and frequency suppression
- Send through tracked email infrastructure

## Follow-Up
Campaign analytics show delivered, opened, clicked, bounced, and unsubscribed outcomes so sales follow-up is based on visible engagement.`,
  },

  // CRM attribution
  {
    id: "crm-attribution-overview",
    title: "Attribution",
    category: "CRM",
    route: "/crm/attribution",
    keywords: ["attribution", "source", "campaign", "revenue", "pipeline", "crm"],
    content: `# Attribution

Attribution shows how sources and campaigns contribute to contacts, leads, pipeline, and won revenue.

## Reading The Report
- Compare source and campaign contribution
- Review pipeline and won-revenue impact
- Use the report as directional evidence for investment decisions

## Boundaries
Attribution depends on CRM source and campaign data being captured on contacts, deals, and campaign sends. Missing source data should be treated as a data-quality issue, not hidden insight.`,
  },

  // Partner vaults (admin-only console)
  {
    id: "partner-vaults-overview",
    title: "Partner vaults",
    category: "Documents",
    route: "/partner-vaults",
    keywords: ["partner", "vault", "workspace", "archive", "personal", "tenant", "admin"],
    actionClaims: ["New workspace", "Archive", "Restore"],
    content: `# Partner vaults

Administrator-only console for the workspaces inside each partner.

## What this page does NOT do
It does not create or decommission a **partner**. A partner is a Postgres schema plus DNS and Static Web App hostname binding, so provisioning stays an operations task — see the *provision a tenant* runbook. A button here could only do part of that job and would leave a half-built partner behind.

## Workspaces
Pick the partner in the **Partner** selector, then use **New workspace** to create one inside it. Everything on the page is scoped to that selection.

## Personal vaults
Tick **Personal vault** and name an owner to make a workspace visible to **that person and administrators only**. This is not a role: a personal vault is not opened by having the right role, by an empty role list, or by any other means. Only the owner and admins see it — including in listings, where it simply does not appear for anyone else.

Only an administrator can create a personal vault owned by someone other than themselves. Staff create **their own** personal workspaces on **My vault**.

## Archiving
Archiving hides a workspace and its documents from everyone except administrators. It is **reversible and non-destructive** — no rows are deleted, and the SharePoint files the documents point at are untouched. Tick **Show archived** to see and restore them.`,
  },

  // My vault (personal workspaces for the signed-in user)
  {
    id: "my-vault-overview",
    title: "My vault",
    category: "Documents",
    route: "/my-vault",
    keywords: ["my vault", "personal", "workspace", "owner", "sharepoint", "team site"],
    actionClaims: ["New personal workspace", "Attach site", "Upload"],
    content: `# My vault

Your **personal** working workspaces inside a partner vault.

## Who can see them
A personal workspace is visible to **you (the owner) and administrators only**. Roles do not open it. Shared team workspaces stay under **Documents → Working**.

## Partner selector
Pick the partner first — personal vaults live in that tenant's schema. Attach and upload use the same \`X-Tenant-ID\` scoping as Documents.

## Create and attach
Use **New personal workspace** to create one owned by you. Then use **Attach site** under Collaboration sites with an **existing** SharePoint Team site URL (Insights does not create M365 sites). **Upload** drafts and open in Office from there.

## Related surfaces
- **Documents → Working** — shared workspaces only
- **Partner vaults** (admins) — create or archive personal vaults for others across partners`,
  },

  // Documents
  {
    id: "vault-overview",
    title: "Document Vault",
    category: "Documents",
    route: "/documents",
    keywords: [
      "document",
      "vault",
      "upload",
      "folder",
      "ocr",
      "search",
      "archive",
      "working",
      "agreements",
      "prepare",
      "signature",
    ],
    actionClaims: [
      "Create Folder",
      "Upload",
      "Prepare for signature",
      "Edit fields",
      "Send for signature",
      "Archive",
      "Restore",
      "Delete",
    ],
    content: `# Document Vault

## Organisation
Pick **Organisation** before creating or uploading (defaults to **Latitude30**). Day-to-day areas such as Internal, Suppliers, or Templates are **folders** under Latitude30 — not separate organisations. Switch organisation only when managing a client vault. **Share with portal users** on a folder publishes to that organisation's portal Documents tab (for example gop → gop.portal…/t/gop/portal/vault).

## Three modes
- **Vault** — immutable finals, managed signature templates, **Edit fields**, **Send for signature**
- **Working** — SharePoint drafts: **Upload**, revise in Office, **Prepare for signature**, **Share with client**
- **Agreements** — optional dossier spine (draft → awaiting signature → completed)

## Folder Structure
Use **Create Folder** to organise documents under the selected organisation. Each folder has role-based visibility — only users with the right role can see the folder and its contents.

## Uploading Documents
1. Navigate to the target folder (Vault) or open a Working workspace
2. Use **Upload** for your document (PDF, images, Word, Excel supported)
3. On Vault, the system automatically extracts text via OCR and generates a summary

## Negotiated agreements → signing
For counsel Word files with their own cover and signature pages: Working → **Prepare for signature** → review the new L30 shell → Promote → **Edit fields** (or use a managed template) → **Send for signature**. See the *Agreement lifecycle* article.

## Extraction & Search
- PDFs and images are automatically OCR'd on upload
- Extracted text is searchable — find documents by their content
- AI generates a summary and detects renewal dates for contracts

## Archive & Restore
Use **Edit fields** for template metadata. Use **Archive** to remove documents from the default view. Admins can toggle the archive view to **Restore** archived documents or **Delete** records that should be permanently removed.

## Audit Trail
Every access, download, and modification is logged in the audit trail (admin-only view).`,
  },
  {
    id: "agreement-lifecycle",
    title: "Agreement lifecycle (negotiate → sign)",
    category: "Documents",
    route: "/documents",
    routes: ["/signatures"],
    keywords: [
      "agreement",
      "nda",
      "msa",
      "mou",
      "contract",
      "prepare",
      "signature",
      "promote",
      "shell",
      "deed",
      "working",
      "counsel",
    ],
    actionClaims: ["Prepare for signature", "Upload", "Send for signature", "Edit fields"],
    content: `# Agreement lifecycle (negotiate → sign)

End-to-end path for NDAs, MSAs, MoUs, and similar agreements.

## 1. Draft on Working
Open **Documents → Working**. **Upload** the negotiated \`.docx\` (SharePoint Team site attached under Collaboration sites). Do not promote the counsel file as-is if it still has its own cover and signature blocks — those duplicate the Latitude30 shell.

## 2. Prepare for signature
On the working row, use **Prepare for signature**:
1. Choose shell topology — NDA or MSA/MoU/Contract style; **1 signer per party** (bilateral) or **2** (deed)
2. Optional **Preview strip** — AI removes letterhead, repeated parties, and signature pages (heuristic fallback if AI is unavailable)
3. **Create signing draft** — a **new** working Word file with L30 cover + your terms + execution block

Greenfield body paste (no source file) is MCP / Ask Insights \`prepare_agreement_from_text\` only.

## 3. Review and negotiate
Open the shell draft in Word. Use **Share with client** for portal review (comment or marked-up return). Revise on SharePoint, then re-share if needed.

## 4. Promote and place fields
Promote the **shell** document to Vault. Prefer a **managed template** (NDA / MSA / MoU / Contract) with fields already placed, or open the Vault PDF → **Edit fields** on the **execution pages**. Party A = Latitude30; Party B fills identity when signing.

## 5. Send
**Send for signature** from Vault (or Signatures). NDA and managed MSA/MoU/Contract pre-authorise L30 (Party A). For MSA/MoU/Contract choose 1 or 2 signers per party in the Send modal. Counterparty signs via email link.

## MCP (PAT files:write)
- \`prepare_agreement_from_working_document\` — file path (\`dryRun: true\` to preview strip)
- \`prepare_agreement_from_text\` — paste body only
- \`promote_working_document\` — copy into Vault`,
  },

  // Document sharing
  {
    id: "sharing-documents-with-clients",
    title: "Sharing documents with clients for review",
    category: "Documents",
    route: "/documents",
    keywords: ["share", "client", "portal", "review", "comment", "working", "vault"],
    content: `# Sharing documents with clients for review\n\nUse **Share with client** on any working draft or finalised Vault document to publish it to a client's portal.\n\n- **Working drafts** share the *current version*. The client always sees exactly that version; upload a new version and re-share to push it.\n- **Finalised documents** share the immutable Vault copy.\n- Choose **Comment** to let the client leave feedback in a shared thread, or **Read** for reference only.\n- **Un-share** revokes access immediately; the discussion is retained for your records.\n\nClient comments appear in the document's thread and notify your team.`,
  },

  // Board Meetings
  {
    id: "board-meetings-overview",
    title: "Board Meetings",
    category: "Board Meetings",
    route: "/board-meetings",
    keywords: ["board", "meeting", "agenda", "snapshot", "commentary", "pack", "pdf"],
    content: `# Board Meetings

## Lifecycle
Meetings follow a strict workflow: **Draft → Published → In Progress → Completed → Archived**

## Creating a Meeting
1. Click **New Meeting** and set the title, date, location, and chair
2. Add agenda sections with items
3. Link vault documents (up to 40)

## Workflow Steps
1. **Take Snapshot**: Captures a point-in-time snapshot of financial and operational data
2. **Generate Commentary**: AI generates executive commentary for each agenda section based on the snapshot data
3. **Generate Pack**: Creates a PDF board pack with cover page, agenda + commentary, actions, and appended vault documents
4. **Publish**: Finalises all assets and makes the meeting visible to board members
5. **Start Meeting → Complete → Archive**: Progress through the lifecycle

## Actions
Track action items with owners and due dates. Open actions are automatically carried forward to the next meeting.`,
  },

  // Signatures
  {
    id: "signatures-overview",
    title: "E-Signatures",
    category: "Signatures",
    route: "/signatures",
    keywords: [
      "signature",
      "sign",
      "esign",
      "legal",
      "certificate",
      "audit",
      "nda",
      "prepare",
      "template",
      "deed",
    ],
    content: `# E-Signatures

Native signing runs on a **Vault PDF** with placed fields — not on Word. Authoring and negotiation happen on Documents → Working first.

## Recommended path (negotiated Word)
1. **Documents → Working** — Upload the counsel file
2. **Prepare for signature** — AI-strip cover/signature duplication into an L30 Word shell
3. Review / portal-share / revise
4. **Promote** to Vault
5. Use a **managed template** (NDA, MSA, MoU, Contract) or **Edit fields** on execution pages
6. **Send for signature** — L30 (Party A) is pre-authorised on NDA and managed commercial templates; choose 1 or 2 signers per party for MSA/MoU/Contract

See the *Agreement lifecycle* article for topology (bilateral vs deed) and MCP tools.

## How send works
1. Select a Vault PDF (or start from Templates)
2. Confirm signature, initials, date, or text fields
3. Assign fields to signers
4. Send — each signer receives a unique signing link (no account needed)

## Signing Process
Signers open their link, view the document, draw their signature, and submit. They must consent to electronic signing. Counterparty legal name and address are filled on the public sign page — staff do not paste Party B identity at send.

## Legal Enforceability
Every signature includes:
- **SHA-256 hash** binding the signature to the document
- **Audit trail** with IP address, timestamp, user agent
- **Certificate of Completion** PDF with all verification details
- **ESIGN Act / eIDAS compliant**: intent, consent, association, retention

## Tamper Detection
After signing, the document's hash is verified. Any modification to the signed PDF will be detected as a hash mismatch.`,
  },

  // Email
  {
    id: "email-overview",
    title: "Tracked Email",
    category: "Tracked Email",
    route: "/email",
    keywords: ["email", "send", "tracking", "open", "click", "compose", "follow-up", "resend"],
    actionClaims: [
      "Compose",
      "Send Email",
      "Follow up",
      "Send again",
      "Archive",
      "Restore",
      "Delete",
    ],
    content: `# Tracked Email

## Composing
Click **Compose** to create a new email. Enter comma-separated recipient emails, a subject, and your message, then use **Send Email**.

## Tracking
Every email sent includes:
- **Open tracking**: A transparent pixel that fires when the email is opened
- **Click tracking**: All links are wrapped to record clicks before redirecting

## Viewing Results
Click any sent email to see tracking events — opens and clicks with timestamps and recipient details.

## Follow-Up
Open a sent email, then use **Follow up** to start a new reply-subject draft to the same recipients, or **Send again** to reuse the original subject and body in a new tracked send.

## Lifecycle
Use **Archive** to remove a sent email from the active list, **Restore** from the archived view, and **Delete** only where admin permissions allow permanent removal.

## Contact History
Each contact's profile shows their email history with open/click engagement metrics.

## Boundaries
Tracked Email is not a mailbox replacement. It does not provide saved drafts, inbound replies, or threaded conversations.`,
  },

  // Cashflow
  {
    id: "cashflow-overview",
    title: "Cash Flow Forecast",
    category: "Cashflow",
    route: "/cashflow",
    keywords: ["cashflow", "forecast", "qbo", "quickbooks", "income", "expense", "balance"],
    actionClaims: [
      "Add Item",
      "Edit",
      "Delete",
      "Save Settings",
      "Export forecast",
      "Export detail",
    ],
    content: `# Cash Flow Forecast

## QuickBooks Connection
Connect your QuickBooks Online account to pull bank balances, recurring transactions, and accounts receivable.

## Manual Items
Use **Add Item** for one-off or recurring (monthly/quarterly/annual) income and expense assumptions. Use **Edit** to correct saved assumptions and **Delete** where the forecast should no longer use them. Items can be in any currency — they're converted to GBP using live exchange rates.

## Forecast
The forecast shows month-by-month projections:
- **Opening Balance**: From QBO bank accounts or manual override
- **Income & Expenses**: Expanded from recurring items
- **Net Flow**: Income minus expenses
- **Closing Balance**: Running total

## Reports & Exports
The report panel summarises the visible forecast horizon with low point, first negative month, net flow, and largest expense month. Use **Export forecast** and **Export detail** to download CSV files from the same visible forecast data.

## Claude for Excel
Admin/exec: add a custom connector at https://api.portal.latitude30.co/api/mcp/cashflow (same Clerk sign-in as the staff app). Ask Claude to pull cashflow_forecast into the open workbook. Sales cannot read cashflow via this path.

## Settings
- Toggle pipeline weighting (include weighted deal values)
- Use a manual opening balance when QBO balances should not drive the planning view
- Use **Save Settings** after changing the forecast controls`,
  },

  // Authenticator
  {
    id: "authenticator-overview",
    title: "Authenticator",
    category: "Workspace",
    route: "/authenticator",
    keywords: ["authenticator", "totp", "code", "qr", "share", "secret"],
    content: `# Authenticator

Authenticator stores team TOTP accounts for internal systems.

## Adding Accounts
Add an account by QR image, camera scan, manual secret entry, or otpauth URI. The page validates Base32 secrets before saving manual entries.

## Using Codes
Current codes refresh in the browser and can be copied from the account card. Account owners can reveal the stored secret, share access with staff emails, or delete accounts they own.`,
  },

  // Receipts
  {
    id: "receipts-overview",
    title: "Receipts",
    category: "Finance",
    route: "/receipts",
    keywords: ["receipt", "expense", "capture", "approve", "reject", "cashflow"],
    content: `# Receipts

Receipts capture expense evidence and move approved items into cashflow.

## Capture
Upload a receipt image or PDF. The page creates a review item after the storage upload succeeds.

## Review
Open a receipt to edit supplier, date, amount, tax, currency, category, payment method, and notes. Approved receipts feed cashflow; rejected receipts remain out of the approved queue.`,
  },

  // Meetings
  {
    id: "meetings-overview",
    title: "Meetings",
    category: "Meetings",
    route: "/meetings",
    keywords: ["meeting", "recording", "download", "archive", "restore", "delete"],
    actionClaims: ["Archive", "Restore", "Delete"],
    content: `# Meetings

Meetings lists uploaded recordings from the standalone recorder.

## Recordings
- Search recordings by title or metadata
- Download ready recordings
- Review platform, status, duration, and recorded date

## Admin Lifecycle
Admins can switch between active and archived views. Active recordings expose **Archive**; archived recordings expose **Restore** or **Delete** where admin permissions allow it.`,
  },

  // Messages
  {
    id: "messages-overview",
    title: "Internal Messaging",
    category: "Messages",
    route: "/messages",
    keywords: ["message", "chat", "channel", "thread", "dm", "direct"],
    content: `# Internal Messaging

## Channels
- **Public**: Visible to all team members
- **Private**: Invite-only, visible to members
- **DM**: Direct messages between two people

## Features
- **Threads**: Reply to any message to start a thread
- **Real-time**: Messages appear instantly via WebSocket
- **Unread counts**: Badge indicators show unread messages per channel
- **Typing indicators**: See when someone is typing

## Creating a Channel
Click the **+** button in the channel sidebar, choose a name and type (public/private).`,
  },
  {
    id: "notes-overview",
    title: "Notes",
    category: "Notes",
    route: "/notes",
    keywords: ["note", "idea", "wikilink", "tag", "search", "detail", "meeting", "research"],
    content: `# Notes

Notes are the written source of truth for lightweight thinking, meeting notes, and idea capture.

## Capture well
- Use **note** for general thinking
- Use **daily** for dated work logs
- Use **meeting** for agendas, outcomes, and follow-ups
- Use **research** for sourced investigation
- Use **idea** for speculative or early insight
- Put important context in the body, not only the title
- Keep tags short and reusable

## Connect notes
- Use [[wikilinks]] for named concepts, people, companies, projects, and related notes
- Unresolved wikilinks are useful open concepts in Ideation
- Backlinks show where other notes refer to the current note
- Pin only the notes that need to stay above normal recency ordering

## Review and lifecycle
- Search by title/body
- Filter by tag or kind
- Archive notes that should leave active workflows but remain auditable
- Delete only when a note is genuinely wrong, duplicate, or unsafe to retain

## Downstream Use
Ideation reads notes, tags, authors, and wikilinks to build the idea graph. Keep durable conclusions in notes or Ideation Map node context rather than only in chat.`,
  },
  {
    id: "ask-insights-overview",
    title: "Ask Insights",
    category: "Insights AI",
    route: "/ai",
    keywords: [
      "ai",
      "ask",
      "insights",
      "claude",
      "skills",
      "artefact",
      "rag",
      "tools",
      "fact ledger",
    ],
    content: `# Ask Insights

Ask Insights is the tenant-scoped staff assistant. One Claude identity serves the tenant; Insights owns conversation history, sources, and artefacts per user.

## What it always does
- Applies **fact-ledger** and **artefact-discipline** skills to every chat
- Thread + sticky composer first; add sources via the strip under the composer (not a top controls card)
- Pins notes, documents, working docs, CRM projects, meetings, deals, **Subjects**, and **Knowledge projects (maps)** — labels stay distinct from CRM
- Page-level Ask Insights entry on notes, vault docs, projects, board meetings, deals, and Subject Workspace
- From a Subject Workspace, Ask pins that Subject node (not an Atlas map). Extra sources are opt-in.
- Can save durable markdown artefacts (right rail when present)

## Flags
- \`insights_ai\` — show the page and allow chat
- \`insights_ai_rag\` — hybrid passage search (ideation corpus + notes/vault/meetings embeddings)
- \`insights_ai_tools\` — allowlisted product tools (create note/task/etc.)
- \`insights_ai_graph_sources\` — SharePoint + Outlook read-only sources (Sites.Selected + mailbox fence)

## Safety
Mutating tools never run silently — you confirm before Insights writes CRM or notes data. Defense classification can refuse packed context that looks like tool-abuse injection.

## Related
Use Ideation for graph exploration; use Notes for durable written truth. Ask Insights is for grounded Q&A and disciplined artefact creation across those surfaces.`,
  },
  {
    id: "ideation-overview",
    title: "Ideation",
    category: "Ideation",
    route: "/ideation",
    keywords: [
      "idea",
      "ideation",
      "knowledge",
      "graph",
      "notes",
      "wikilink",
      "claude",
      "cluster",
      "board",
    ],
    content: `# Ideation

Ideation turns team notes into a network of ideas, tags, authors, and linked CRM/project work. Use the **? on each panel** for inline help, or **Replay tour** (this drawer) for the guided walkthrough.

## Source model
- Notes are still the source of truth.
- Tags become concept nodes.
- [[Wikilinks]] become graph connections.
- Authors show where team thinking is coming from.

## The workflow
1. **Capture** a thought as a note — use [[wikilinks]] and tags to connect it.
2. **Explore the graph** — it's force-directed, so connected ideas sit together. Drag to pan, scroll to zoom, and use **Fit** to frame everything; click a node to inspect it.
3. **Focus** with search/tag filters, and **save** a useful view as a board.
4. **Cluster** — let Claude group related notes into draft themes you review and approve.
5. **Hand off** an approved cluster or a node to a decision log, a client portal brief, or a project discovery brief.

## Review rules
- Claude answers are a thinking aid until captured or approved
- Capture durable answers as idea notes
- Approved clusters can move into reviewed handoff workflows
- Client-visible output must go through the relevant review and publish path

## Guided next steps
The **Suggested next steps** card surfaces the most useful action for the current state — e.g. "Generate draft clusters" once you have enough notes. Dismiss any you don't want.`,
  },
  {
    id: "ideation-maps-overview",
    title: "Ideation Maps",
    category: "Ideation",
    route: "/ideation/maps",
    keywords: [
      "ideation",
      "maps",
      "knowledge",
      "graph",
      "node",
      "edge",
      "hub",
      "flow",
      "document",
      "extraction",
      "claude",
    ],
    content: `# Ideation Maps

Ideation Maps (product name **Atlas**) are the curated graph workspace. Use them when the relationship itself is part of the knowledge, not just a projection from notes.

Open via **Jump to…** and search **Atlas**, **maps**, or **hub** (Ctrl+K / ⌘K).

## Map types
- **Hub** maps are indexes and maps-of-maps
- **Graph** maps show networks of related nodes
- **Flow** maps show ordered or narrative relationships
- **Freestyle** maps are looser canvases for early structure

## Nodes and relationships
- A node is one shared identity and can appear in many maps
- Use concise labels and stable node kinds
- Add a short summary for neighbouring context
- Add markdown context for durable detail
- Connect nodes with typed relationships such as depends-on, implements, references, questions, addresses, supersedes, or relates-to

## Documents and evidence
- Upload or attach Vault documents to the relevant node
- Let extraction finish when text is needed
- Refer to the document in node context
- Keep raw files in Vault/Blob rather than pasting full documents into node bodies

## Claude and extraction
- Preview assembled context when the answer depends on exact grounding
- Ask grounded questions over the selected node or map
- Promote durable conclusions into node context or Notes
- Run extraction from chat, text, transcript, or document-derived content
- Review every proposed node, edge, and context addition before applying

## Lifecycle
- Remove from map when the node does not belong in this view
- Delete everywhere only when the identity itself should be removed
- Merge duplicates into the canonical node
- Use cross-map doorways to inspect other views of the same identity`,
  },
  {
    id: "notetaking-ideation-kit",
    title: "Note-Taking and Ideation Kit",
    category: "Ideation",
    routes: ["/notes", "/ideation", "/ideation/maps"],
    keywords: [
      "notes",
      "note",
      "ideation",
      "maps",
      "kit",
      "instructions",
      "guide",
      "workflow",
      "wikilink",
      "claude",
      "document",
      "extraction",
    ],
    content: `# Note-Taking and Ideation Kit

The kit has four layers: Notes capture the durable record, classic Ideation projects a live graph from notes, Ideation Maps curate durable graph structure, and Vault documents provide source evidence.

## Choose the right surface
- Use **Notes** for meetings, daily logs, research, decisions, and early ideas
- Use **classic Ideation** to see the graph implied by notes, tags, authors, and wikilinks
- Use **saved boards** to preserve a temporary graph view
- Use **Ideation Maps** for curated research models, typed relationships, node context, and reviewed graph edits
- Use **Vault documents** when a claim needs evidence or source material

## Capture
- Give notes and nodes specific names
- Use stable tags, not sentences
- Use [[wikilinks]] for concepts, people, companies, projects, and related notes
- Capture important Claude answers as idea notes or node context
- Do not leave durable decisions only in chat

## Curate
- Reuse existing map nodes when the identity already exists
- Merge duplicates rather than keeping parallel versions
- Remove from a map when the node only does not belong in that view
- Delete everywhere only when the identity should disappear everywhere
- Attach source documents to the node they support

## AI review
- Treat AI output as a proposal
- Require reviewed diffs before extraction changes the graph
- Accept, edit, or reject each proposed item
- Keep source labels visible when answers depend on evidence
- Publish client-visible output only through the board, portal brief, or project review workflow

## Quick recipes
- Meeting to graph: create a meeting note, add tags and wikilinks, then inspect it in Ideation
- Claude answer to durable record: ask, inspect sources, then capture as an idea note or node context
- Document-backed research: attach the document to a node, ask/extract from grounded context, review the diff, then apply
- Duplicate cleanup: choose the canonical node, merge duplicates into it, and check maps that referenced either node`,
  },
  {
    id: "scheduling-overview",
    title: "Scheduling",
    category: "Scheduling",
    route: "/scheduling",
    keywords: ["scheduling", "availability", "booking", "event", "calendar", "slots"],
    content: `# Scheduling

Scheduling manages event types, weekly availability, and booked sessions.

## Staff Setup
- Create, edit, and archive event types
- Add and remove weekly availability windows
- Review upcoming bookings from the scheduling page

## Public Booking
Each event type has a public booking URL. Invitees pick an available slot, enter their details, and receive the booking result from the public flow.`,
  },
  {
    id: "time-tracking-overview",
    title: "Time Tracking",
    category: "Time Tracking",
    route: "/time-tracking",
    keywords: ["time", "timer", "timesheet", "ledger", "billable", "export"],
    actionClaims: [
      "Start timer",
      "Stop",
      "Add entry",
      "Save entry",
      "Save preferences",
      "Export CSV",
      "Delete",
    ],
    content: `# Time Tracking

Time tracking combines the header timer with a filterable ledger for completed work.

## Entries
- Use **Start timer** in the header to open the quick timer form
- Use **Stop** on the running timer when work is complete
- Use **Add entry** for manual entries from the time page
- Use **Save entry** when correcting completed entries
- Use **Delete** for completed entries that should not remain in the ledger
- Use **Save preferences** after changing rate, currency, week-start, or rounding settings

## Reports
Filter by date, project, and user. Day and project totals update from the visible entries, and **Export CSV** matches the same filtered ledger.`,
  },
  {
    id: "portal-users-overview",
    title: "Portal Users",
    category: "Portal",
    route: "/portal-users",
    keywords: ["portal", "client", "invite", "workspace", "magic link", "archive"],
    actionClaims: [
      "New partner portal",
      "Invite portal user",
      "Provision",
      "Save",
      "Resend invite",
      "Archive",
      "Unarchive",
    ],
    guideTargets: [
      {
        intent: "Choose the partner portal",
        route: "/portal-users?helpAnchor=portal-partner-selector",
        controlId: "portal-partner-selector",
      },
      {
        intent: "Invite a portal user",
        route: "/portal-users?helpAnchor=portal-user-actions",
        controlId: "portal-user-actions",
      },
    ],
    content: `# Portal Users

Portal users manages external access to the customer portal.

## New partner portal
The partner shown on Portal Users is the portal you are managing, not the one you are creating. Belsize appears there when that was the last partner you opened. Leave that selection alone.

Admins use **New partner portal** (Portal Users, or **Jump to…** / Ctrl+K) to open the full-page wizard. **Tenant slug** is the new partner, with family \`partners\`, \`devices\`, or \`gop\`. **Source tenant for documents** is \`l30\`. Do not copy documents from Belsize or another partner. **Fill Belsize example** only recreates the Belsize portal. Preview the run, retype the new slug, then **Provision**. Invite users only after that tenant exists, and invite them into the new partner, not into the one that was already selected. Until the l30.solutions cutover, partners-family tenants open on \`partners.portal.latitude30.co/t/<slug>\`.

## Workspace
Select a **Partner** first (e.g. \`gop\` for Transition33, or a devices-family vendor). Use **Provision** for a new portal workspace, or **Save** after updating purpose, package, accent colour, or enabled state where admin permissions allow it. The **Components** checklist controls navigation, landing-page sections, Help content, Ask publication actions, and API access. Dependencies are selected automatically; for example, document review requires documents. The page no longer auto-picks the first alphabetical tenant — that previously silently selected \`devices\` ahead of \`gop\`.

## Users
Use **Invite portal user** (or **Jump to…** / Ctrl+K → **Invite portal user…**) and choose the **Partner portal** in the dialog (URL preview shows \`gop.portal…/t/<slug>\`, \`devices.portal…/t/<slug>\`, or \`partners.portal…/t/<slug>\`). Then enter name, email, and role. Use **Resend invite** from the user row. Admins can use **Archive** to revoke access and **Unarchive** archived users from the archived view.

## Publishing with Ask Insights
Ask Insights can also preview and, after you confirm, provision a collaboration portal. That uses the same wizard as **New partner portal**: tenant, family, workspace, documents, then invites. It does not bind a hostname. Open **Ask Insights** and ask it to list portal engagements, publish candidates, or existing publications. Ask only offers publication actions supported by that portal's enabled Components. For a write, ask it to preview the exact partner, object, and access level first. **Publish** and **revoke** actions always show an explicit confirmation; the signed confirmation expires and is bound to your user, tenant, and conversation. Subject publication is intentionally unavailable until the portal has a self-contained subject projection.`,
  },
  {
    id: "portal-partner-onboarding",
    title: "Onboarding a Partner to the Client Portal",
    category: "Portal",
    route: "/portal-users",
    keywords: ["onboard", "partner", "provision", "tenant", "portal", "magic link", "invite"],
    actionClaims: [
      "New partner portal",
      "Provision",
      "Save",
      "Invite portal user",
      "Resend invite",
    ],
    content: `# Onboarding a Partner to the Client Portal

The end-to-end flow that takes a new partner from nothing to signing in to their branded portal. Partners share family hosts — no per-partner DNS or certificate work is needed.

## 1. New partner portal (Insights admin)
Open **Portal Users** and click **New partner portal**, or search **Jump to…** (Ctrl+K / ⌘K) for onboard / wizard. The partner already selected on Portal Users is not the tenant you are creating. Ask Insights can run the same preview and confirmed provision for an admin.

- **Tenant slug** is the new partner. Family is \`partners\`, \`devices\`, or \`gop\`.
- **Source tenant for documents** is \`l30\`. Do not use the partner shown in the header, and do not use Belsize, as the source.
- **Fill Belsize example** loads the existing Belsize portal. Leave it alone unless you mean to recreate that portal.

Then fill workspace, optional shared project, and invitees. Preview, retype the new slug, then **Provision**. Invites are sent last so a partner never receives a link to an empty portal. Azure \`BOOTSTRAP_TENANT\` remains the fallback when the wizard cannot run, and its value is the new slug, not \`l30\`.

## 2. Configure the portal workspace
The wizard already created or updated the workspace. Switch to the new tenant if you need to tweak name, package, accent colour, or enabled state, then **Save**. Use **Provision** only when the page reports that no workspace exists for the tenant.

## 3. Invite extra portal users
Use **Invite portal user** (Portal Users, or **Jump to…** / Ctrl+K) with a name, email, and role. Each invite emails the user a magic link. Use **Resend invite** from the user row if the email is missed. Never add portal users directly in the database — the invite flow creates the audit trail and the sign-in email.

## 4. Share the portal link
Canonical hosts (after DNS + certs, then \`PORTAL_DOMAIN_ROOT\` cutover):

- Partners: \`https://partners.portal.l30.solutions/t/<slug>/portal\`
- Vendors / devices: \`https://devices.portal.l30.solutions/t/<slug>/portal\`
- GoP group (gop, moip, mocc): \`https://gop.portal.l30.solutions/t/<slug>/portal\`

Until cutover, live links stay on \`*.portal.latitude30.co\` (\`partners.portal…/t/<slug>\`, \`devices.portal…/t/<slug>\`, \`gop.portal…/t/<slug>\`). Magic-link emails redirect to the correct tenant path automatically.

## 5. Verify and finish
Confirm the invited user receives the email, clicks the link, and lands on their branded portal. Then open the Engagement Plan page and add the first milestones and stakeholders so the client does not land on an empty plan.

## If something fails
- **No email**: check the address on the user row, then the user's spam folder; resend the invite.
- **"Not signed in" after clicking the link**: usually a cookie issue — retry in a normal (non-private) browser window.
- **Wrong tenant or brand**: check the \`/t/<slug>\` part of the link matches the tenant slug.`,
  },
  {
    id: "engagement-plan-overview",
    title: "Engagement Plan",
    category: "Portal",
    route: "/engagement-plan",
    keywords: [
      "engagement",
      "milestone",
      "stakeholder",
      "brief",
      "portal",
      "publish",
      "getting started",
      "client view",
    ],
    actionClaims: [
      "Add stakeholder",
      "Add milestone",
      "Save draft",
      "Publish + notify",
      "Publish",
      "Archive",
      "Restore",
      "Delete",
      "Looks good — continue",
      "Client view",
      "Regenerate",
    ],
    guideTargets: [
      {
        intent: "Open the partner and engagement selector",
        route: "/engagement-plan?helpAnchor=engagement-partner-selector",
        controlId: "engagement-partner-selector",
      },
      {
        intent: "Open Share and Client view",
        route: "/engagement-plan?helpAnchor=engagement-workspace",
        controlId: "engagement-workspace",
      },
    ],
    content: `# Engagement Plan

Engagement plan is where staff stand up a partner's Client Hub, then maintain brief, share, and collaborate day to day.

## Getting started
- Work through **Invite → Brief → Share**, then confirm **Client view** on the right
- When ready, choose **Looks good — continue** — Getting started collapses to a readiness chip
- Reopen anytime from the chip in the header

## Client view
- The **Client view** rail is a live, read-only mirror of the partner Home hub (no new tab, no guest sign-in)
- Publishing the brief or sharing items updates the rail without leaving the page
- Use **Open real portal** only for edge debugging (requires guest login)

## Workspace
- **Overview** — inbox, brief status, readiness, next milestone
- **Brief** — edit overview fields and **Client-facing sections** (title, body, Include on portal), then **Save draft** or **Publish + notify**. **Regenerate** rebuilds section copy from Plan / Share data (confirm first — it overwrites edits)
- **Plan** — **Add stakeholder** and **Add milestone**
- **Share** — **Publish** portal objects
- **Collaborate** — messages and client inbox uploads

## Admin Lifecycle
Admins can show archived rows, **Archive** or **Restore** plan items, and **Delete** archived stakeholders or milestones where the UI exposes the action.`,
  },
  {
    id: "programme-collaboration-overview",
    title: "Committees & working groups",
    category: "Portal",
    route: "/programme-collaboration",
    keywords: [
      "programme",
      "collaboration",
      "body",
      "working group",
      "committee",
      "chair",
      "roster",
      "meeting",
      "agenda",
      "attendance",
      "minutes",
      "actions",
      "gop",
      "admin",
    ],
    content: `# Committees & working groups

**Client Portal → Committees & working groups** (/programme-collaboration) is the staff admin surface for standing up a partner programme's governance bodies — committees and working groups — and running their meetings. Seated portal guests see **both kinds** they sit on under **Committees & working groups** in the partner portal. It is deliberately **not** Board Meetings and **not** the Documents SharePoint-URL attach form. Body membership is a separate concept from the engagement's stakeholder roster — someone can be a body chair without being an engagement stakeholder, and vice versa.

## Bodies
- Pick a **Partner** and its engagement, then create a **committee** or **working group**
- A body may have **at most one active chair** at a time — add members, observers, and secretaries too; a chair is recommended for committee ceremony and minutes approval, but not required when you create the body
- Seat portal users via their **contact email** (must match their magic-link login) so the body appears on their portal page
- When adding members, the **L30 contact**, or an action owner: pick **Contacts** (partner CRM) or **Staff** (Latitude30 directory — linked onto the partner as a contact automatically)
- A person can sit on several bodies with a different role on each; body membership is separate from the engagement's stakeholder roster

## What seated guests see
- **Terms of reference** — pin a Vault or working document as the body's TOR; pinning also shares it with the engagement
- **Who to contact** — set the named Latitude30 contact for the body
- **Meeting pack** — date, place, full agenda, and any papers attached to agenda items in **Prepare**
- **Approved minutes** — readable once the chair or secretary approves them (drafts stay staff-side)
- **Open actions** — with owner and due date, plus recently completed ones
- **Shared files** — documents in the body's attached shared folder

## Running a committee meeting
Committees step through **Prepare → Attend → Record → Close**:
1. **Prepare** — build the agenda and link relevant papers
2. **Attend** — take roster attendance (present / apologies / absent / proxy), or **Mark all present**
3. **Record** — draft the minutes and raise action items
4. **Close** — the chair or secretary **Approves minutes** (immutable once approved); open actions carry forward to the next meeting automatically

Working groups default to a lighter single **Notes** view — full minutes-approval ceremony is optional for them.

## Getting started
The first-run wizard walks **Create body → Add a chair → Add members → Schedule meeting**. Use **Looks good — continue** to collapse it to a readiness chip, reopened anytime from the header.

## Boundaries (today)
- **Room** (co-authoring) does not exist yet in-app — manage SharePoint/guest access outside Insights for now; the member-row invite chip lands in a later release
- The portal read is read-only: guests never mark attendance, edit minutes, or close actions
- Nested subcommittees and voting are not modelled`,
  },
  {
    id: "settings-overview",
    title: "Settings and Usage",
    category: "Settings",
    route: "/settings",
    keywords: ["settings", "usage", "quickbooks", "integration", "admin", "tokens", "audit"],
    actionClaims: [
      "Add staff user",
      "Apply",
      "Load older",
      "Save scoring policy",
      "Save",
      "Connect to QuickBooks",
      "Clear configuration",
      "Start PoC database",
      "Stop PoC database",
      "Refresh status",
    ],
    content: `# Settings and Usage

Settings gives admins and operators a view of tenant configuration, usage, and integration state.

## Operational Settings
Use this page to review usage and integration configuration that affects the staff app. Admin-only controls remain protected by the API even when a user can open the settings page.

## Admin Configuration
- Use **Add staff user** to invite or update a Clerk staff user's Insights role
- Use **Apply** to filter the tenant audit log by resource, action, or actor
- Use **Load older** to page through earlier audit events
- Use **Save scoring policy** to persist CRM scoring policy changes
- Use **Save** to store QuickBooks app credentials
- Use **Connect to QuickBooks** after credentials are configured
- Use **Clear configuration** to remove saved QuickBooks credentials before OAuth connection
- Use **Start PoC database** / **Stop PoC database** to wake or pause the dedicated Transition33 PoC Postgres server (never the Insights application database)
- Use **Refresh status** to re-read that PoC server's Azure state

## Token Settings
The API tokens view under settings manages staff-created tokens. Use it for controlled API access rather than sharing personal credentials.`,
  },
  {
    id: "settings-tokens-overview",
    title: "API Tokens",
    category: "Settings",
    route: "/settings/tokens",
    keywords: ["api", "token", "settings", "credentials", "access", "mcp", "claude desktop", "pat"],
    content: `# API Tokens

Personal Access Tokens (PATs) give controlled programmatic access for staff workflows — including Claude Desktop MCP.

Open via **Jump to…** and search **tokens**, **PAT**, or **MCP** (Ctrl+K / ⌘K), or Settings → Personal Access Tokens.

## Claude Desktop / MCP
1. Open New access token and choose preset Claude Desktop / MCP (locks ideation:write and files:write).
2. Copy the token and the Desktop mcp-remote config snippet shown once after create.
3. Merge into %APPDATA%\\Claude\\claude_desktop_config.json, fully quit and restart Desktop.
4. Prod MCP URL: https://api.portal.latitude30.co/api/mcp.

## Scopes
- ideation:write — capture_knowledge / capture_decision (reviewed-diff pipeline).
- files:write — create_sharepoint_folder / create_sharepoint_file / create_working_document, adopt_working_documents, revise_working_document, comment_on_working_document, prepare_agreement_from_text, prepare_agreement_from_working_document, promote_working_document. Uploads auto-rename on name clash (never overwrite).
- graph:write — upsert_node / upsert_edge / supersede_node / supersede_fact (structured graph writes, stored verbatim with source + date on every fact). Pre-ticked on the preset but optional: untick it to issue a connector that can read and capture but holds no device/cost authority.

## Two write paths (which tool for what)
- Rationale, analysis, session narrative → capture_decision / capture_knowledge. Inferred, confidence-banded, waits for review.
- Facts that must be exact — part numbers, costs, origin, thermal grades, rule numbers, statuses, relationships → upsert_node / upsert_edge. Stored exactly as sent, immediately, with source + asOf required per fact.
- A value that has changed → supersede_node, never a delete. The old node keeps its history and gains a marker that every read leads with.
- A single fact that was renamed or corrected on the same day → supersede_fact. The row stays and is labelled withdrawn. Current reads skip it. The rest of the node stays current.
- Completeness questions (full BOM, every device using a part) → query_nodes, which is exhaustive and states the total. search_knowledge is ranked and trimmed, so it answers "what is relevant", not "what is all of it".
- Every structured write takes dryRun: true to preview the diff, and reads the stored row back after applying.

## Capture ritual (after a decision in Desktop)
Desktop does not stream every message into Insights. After an important decision:
1. list_maps — note the project map id
2. Prefer capture_decision (decision + mapId; optional rationale/owners/context). Use capture_knowledge for free-form text or end-of-session rawSource + format claude-ai-json. PAT must have ideation:write.
3. Open **Review** in Atlas (floating control or toast) → Accept / Apply when ready

High-confidence items may auto-apply per the map's curation policy; everything else waits for review.

## Token Handling
Create tokens only for a specific need, store the value securely when it is shown, and revoke tokens that are no longer needed. Do not use personal passwords or portal magic links for integrations.`,
  },
];

// ─── Route Mapping ─────────────────────────────────────────

/**
 * Get help articles relevant to the current route.
 */
export function getArticlesForRoute(route: string): HelpArticle[] {
  const articleRoutes = (article: HelpArticle): string[] => {
    const routes = article.routes ?? [];
    return article.route ? [article.route, ...routes] : routes;
  };

  // Exact match first
  const exact = helpArticles.filter((a) => articleRoutes(a).includes(route));
  if (exact.length > 0) return exact;

  const prefix = helpArticles.filter((a) =>
    articleRoutes(a).some(
      (articleRoute) => articleRoute !== "/" && route.startsWith(`${articleRoute}/`),
    ),
  );
  if (prefix.length > 0) return prefix;

  // Default to getting started
  return helpArticles.filter((a) => a.id === "getting-started");
}

/**
 * Search help articles by keyword.
 */
export function searchArticles(query: string): HelpArticle[] {
  const q = query.toLowerCase();
  return helpArticles
    .map((article) => {
      let score = 0;
      if (article.title.toLowerCase().includes(q)) score += 10;
      if (article.content.toLowerCase().includes(q)) score += 5;
      for (const kw of article.keywords) {
        if (kw.includes(q) || q.includes(kw)) score += 3;
      }
      return { article, score };
    })
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((r) => r.article);
}
